import type { PortableTextBlock, PortableTextSpan } from '@portabletext/types';

const SECTION_NUMBER_PREFIX = /^(\d+(?:\.\d+)*)\.\s+/;

const HEADING_STYLES = new Set(['h2', 'h3', 'h4']);

export type LegalTocEntry = {
  id: string;
  /** Full heading text as authored */
  title: string;
  /** Nesting derived from numbering (`1` → 0, `1.1` → 1) or heading level fallback */
  depth: number;
};

export type LegalHeadingAnchorMap = Record<string, string>;

export function numberingToSectionId(num: string): string {
  return `section-${num.replace(/\./g, '-')}`;
}

function slugifyHeading(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 72);
}

function blockToPlainText(block: PortableTextBlock): string {
  return block.children
    .map((child) => {
      const span = child as PortableTextSpan;
      return typeof span?.text === 'string' ? span.text : '';
    })
    .join('');
}

function headingStyleFallbackDepth(style: string | undefined): number {
  if (style === 'h2') return 0;
  if (style === 'h3') return 1;
  if (style === 'h4') return 2;
  return 0;
}

/**
 * Builds stable `section-*` ids from leading numbering (e.g. `1. Foo` → `section-1`, `1.2. Bar` → `section-1-2`)
 * and a TOC list for sticky navigation. Requires Sanity block `_key`s for renderer lookup.
 */
export function buildLegalHeadingNav(copy: PortableTextBlock[]): {
  toc: LegalTocEntry[];
  idByBlockKey: LegalHeadingAnchorMap;
} {
  const toc: LegalTocEntry[] = [];
  const idByBlockKey: LegalHeadingAnchorMap = {};
  const usedIds = new Set<string>();
  let slugFallbackCounter = 0;

  for (const raw of copy) {
    if (!raw || raw._type !== 'block') continue;

    const block = raw as PortableTextBlock;
    if (block.listItem) continue;

    const style = block.style;
    if (!style || !HEADING_STYLES.has(style)) continue;

    const text = blockToPlainText(block).trim();
    if (!text) continue;

    const key = typeof block._key === 'string' ? block._key : '';
    if (!key) continue;

    const match = text.match(SECTION_NUMBER_PREFIX);
    let baseId: string;
    let depth: number;

    if (match) {
      baseId = numberingToSectionId(match[1]);
      depth = Math.max(0, match[1].split('.').length - 1);
    } else {
      slugFallbackCounter += 1;
      const slug = slugifyHeading(text);
      baseId = `section-${slug || `heading-${slugFallbackCounter}`}`;
      depth = headingStyleFallbackDepth(style);
    }

    let id = baseId;
    let suffix = 2;
    while (usedIds.has(id)) {
      id = `${baseId}-${suffix}`;
      suffix += 1;
    }
    usedIds.add(id);

    idByBlockKey[key] = id;
    toc.push({ id, title: text, depth });
  }

  return { toc, idByBlockKey };
}
